import { useEffect, useState, lazy, Suspense } from "react"
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar"
import { AppSidebar } from "@/components/AppSidebar"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { useAuth } from "@/lib/auth"
import { supabase } from "@/lib/supabase"
// OPTIMIZED: Lazy load heavy AssistantFab component
const AssistantFab = lazy(() => import("@/components/AssistantFab").then(m => ({ default: m.AssistantFab })))

interface DashboardLayoutProps {
  children: React.ReactNode
  hideHeader?: boolean
  sidebarCollapsed?: boolean
}

export function DashboardLayout({ children, hideHeader = false, sidebarCollapsed = false }: DashboardLayoutProps) {
  return (
    <SidebarProvider defaultOpen={!sidebarCollapsed}>
      <AppSidebar hoverMode={sidebarCollapsed} />
      <SidebarInset className="dark:bg-sidebar-background">
        {!hideHeader && (
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <div className="ml-auto flex items-center gap-2">
              <CreditsIndicator />
            </div>
          </header>
        )}
        <div className={`relative flex flex-1 flex-col overflow-x-auto overflow-y-auto ${hideHeader ? '' : 'p-4 md:p-6'}`}>
          {children}
        </div>
        {/* OPTIMIZED: Lazy loaded assistant */}
        <Suspense fallback={null}>
          <AssistantFab />
        </Suspense>
      </SidebarInset>
    </SidebarProvider>
  )
}

function CreditsIndicator() {
  const { user } = useAuth()
  const [balance, setBalance] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    let mounted = true
    const fetchBalance = async () => {
      if (!user) {
        if (mounted) setLoading(false)
        return
      }
      const { data } = await supabase
        .from('usage_accounts')
        .select('current_balance')
        .eq('user_id', user.id)
        .single()
      if (mounted) {
        setBalance(data?.current_balance ?? 0)
        setLoading(false)
      }
    }
    fetchBalance()
    const handler = () => fetchBalance()
    window.addEventListener('credits:refresh', handler)
    return () => { mounted = false; window.removeEventListener('credits:refresh', handler) }
  }, [user])

  return (
    <Badge variant="secondary">
      Créditos: {loading ? '...' : (balance ?? '—')}
    </Badge>
  )
}
